import {Box, Button, Divider, Grid, MenuItem, TextField, Theme, Typography} from '@mui/material';
import {SxProps} from '@mui/system';
import {ExportToCsv, useApiContainer, useUserInfo} from '@ozark/common';
import {Loading, Table, Title} from '@ozark/common/components';
import {ResidualGroupOverride} from '@ozark/functions/src/functions/express/private/types/Residual';
import {CancelTokenSource} from 'axios';
import {parse} from 'date-fns';
import {format} from 'date-fns-tz';
import {isEmpty} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import {Column} from '../../../api/Column';
import {CancelOperationMessage} from '../../../api/Constants';

const tableSx: SxProps<Theme> = {
  width: '600px',
};

interface Props {
  yearMonths: string[] | null;
  loadingResiduals: boolean;
}

export const ResidualGroupOverrides = ({yearMonths, loadingResiduals}: Props) => {
  const [residualOverrides, setResidualOverrides] = useState<ResidualGroupOverride[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [_, setCancelTokenSource] = useState<CancelTokenSource | undefined>();
  const {isErpUser} = useUserInfo();
  const api = useApiContainer();
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>();

  const columnsConfig = isErpUser ? erpColumnsConfig : agentColumnsConfig;

  useEffect(() => {
    if (loadingResiduals || !yearMonths) {
      return;
    }
    setSelectedYearMonth(yearMonths[yearMonths.length - 1]);
  }, [loadingResiduals, yearMonths]);

  const exportToCsv = useCallback(
    async (residualOverride: ResidualGroupOverride) => {
      if (!api) return;
      const groupId = residualOverride.firestoreParentGroupId;
      const childGroupId = residualOverride.firestoreChildGroupId;
      const yearMonth = residualOverride.yearMonth;

      const options = {
        fieldSeparator: ',',
        filename: `residualsOverrides-${yearMonth}`,
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        showTitle: false,
        title: 'Overrides',
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
      };

      const exportData = await api.residuals.getGroupOverridesExport(
        groupId,
        childGroupId,
        yearMonth
      );

      if (!exportData || exportData.length === 0) return;

      const exporter = new ExportToCsv(options);

      const noNullData = JSON.parse(JSON.stringify(exportData).replace(/null/gi, '""'));

      exporter.generateCsv(noNullData);
    },
    [api]
  );

  const cancelRequests = () => {
    const cancelSource = api!.residuals.getCancelTokenSource();
    setCancelTokenSource(prev => {
      //Check if there are any previous pending requests
      if (prev !== undefined) {
        prev.cancel(CancelOperationMessage);
      }
      return cancelSource;
    });
    return cancelSource;
  };

  const handleDateChangeWithChangeValidation = (event: any) => {
    const yearMonth = event.target.value;
    setSelectedYearMonth(yearMonth);
  };

  const getGroupOverrides = async (yearMonth: string | undefined) => {
    if (!api || !yearMonth) {
      return;
    }
    const cancelRequestToken = cancelRequests();
    setIsLoading(true);
    try {
      const response = await api.residuals.getGroupOverrides(yearMonth, cancelRequestToken.token);
      setResidualOverrides(response ?? []);
      setIsLoading(false);
    } catch (err: any) {
      if (err?.message === CancelOperationMessage) {
        return;
      }
      console.error(err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getGroupOverrides(selectedYearMonth);
  }, [selectedYearMonth]);

  if (isLoading || loadingResiduals) return <Loading />;

  if (!yearMonths || yearMonths.length === 0) {
    return (
      <Box mt={6}>
        <Typography variant="body1" align="center">
          No residuals have been added.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Title breadcrumbs={[<Typography variant="body1">Group Overrides</Typography>]}>
        <Box sx={{flex: 1}} />
        <Divider
          orientation="vertical"
          sx={theme => ({
            margin: theme.spacing(2),
          })}
          flexItem
        />
        {yearMonths && (
          <TextField
            value={selectedYearMonth}
            onChange={handleDateChangeWithChangeValidation}
            variant="standard"
            InputProps={{
              sx: {
                backgroundColor: 'transparent !important',
              },
              disableUnderline: true,
            }}
            select
          >
            {yearMonths.map(e => {
              return (
                <MenuItem key={e} value={e}>
                  {formatYearMonth(e)}
                </MenuItem>
              );
            })}
          </TextField>
        )}
        <Divider
          orientation="vertical"
          sx={theme => ({
            margin: theme.spacing(2),
          })}
          flexItem
        />
      </Title>
      {residualOverrides && isEmpty(residualOverrides) && (
        <Typography
          sx={{
            top: '40%',
            position: 'relative',
            textAlign: 'center',
          }}
        >
          No Overrides
        </Typography>
      )}
      {residualOverrides && !isEmpty(residualOverrides) && (
        <Grid container spacing={2} direction="row" alignItems="stretch">
          <Grid item xs={12}>
            <Table
              stickyHeader
              scrollableBody
              customHeight="75vh"
              columns={columnsConfig}
              rows={residualOverrides}
              tableProps={{sx: tableSx}}
              actions={row => {
                return (
                  <Box sx={{textAlign: 'center'}}>
                    <Button color="primary" variant="contained" onClick={e => exportToCsv(row)}>
                      Export
                    </Button>
                  </Box>
                );
              }}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const formatYearMonth = (yearMonth: string) => {
  const date = parse(yearMonth as string, 'yyyyMM', new Date());
  return format(date, 'MMMM, yyyy');
};

const formatCurrency = (value?: number) => {
  if (!value && value !== 0) return '';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return formatter.format(value);
};

const erpColumnsConfig: Column<ResidualGroupOverride>[] = [
  {
    id: 'yearMonth',
    label: 'Residual Date',
    width: 200,
    selector: row => formatYearMonth(row.yearMonth),
  },
  {
    id: 'parentGroupName',
    label: 'Override Group',
    width: 150,
  },
  {
    id: 'childGroupName',
    label: 'Referred Group',
    width: 150,
  },
  {
    id: 'transactionCount',
    label: 'Transactions',
    width: 150,
    numeric: true,
    selector: row => Number(row.transactionCount ?? '0'),
  },
  {
    id: 'grossSales',
    label: 'Gross Sales',
    width: 150,
    numeric: true,
    selector: row => formatCurrency(Number(row.grossSales ?? '0')),
  },
  {
    id: 'amount',
    label: 'Override',
    width: 150,
    numeric: true,
    selector: row => formatCurrency(Number(row.amount ?? '0')),
  },
];

const agentColumnsConfig: Column<ResidualGroupOverride>[] = [
  {
    id: 'yearMonth',
    label: 'Residual Date',
    width: 200,
    selector: row => formatYearMonth(row.yearMonth),
  },
  {
    id: 'childGroupName',
    label: 'Referred Group',
    width: 150,
  },
  {
    id: 'transactionCount',
    label: 'Transactions',
    width: 150,
    numeric: true,
    selector: row => Number(row.transactionCount ?? '0'),
  },
  {
    id: 'grossSales',
    label: 'Gross Sales',
    width: 150,
    numeric: true,
    selector: row => formatCurrency(Number(row.grossSales ?? '0')),
  },
  {
    id: 'amount',
    label: 'Override',
    width: 150,
    numeric: true,
    selector: row => formatCurrency(Number(row.amount ?? '0')),
  },
];
